<script setup>
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@chatwoot/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import AgentLifecycleDialog from './AgentLifecycleDialog.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMasterProfile } from 'dashboard/composables/useAccountResource';

const getters = useStoreGetters();
const store = useStore();
const isMaster = useMasterProfile();
const { t } = useI18n();

const showAddPopup = ref(false);
const showTransferDialog = ref(false);
const showEditPopup = ref(false);
const currentAgent = ref({});
const searchQuery = ref('');

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (agent.is_master) return t('ACCOUNT_RESOURCES.MASTER');

  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const showEditAction = agent => {
  return (
    (currentUserId.value !== agent.id ||
      isMaster.value ||
      agent.can_assign_master) &&
    (!agent.is_master || isMaster.value)
  );
};

const showDeleteAction = agent =>
  isMaster.value && !agent.is_master && agent.id !== currentUserId.value;

const canResetPassword = agent =>
  isMaster.value &&
  !agent.is_master &&
  agent.id !== currentUserId.value &&
  agent.provider === 'email';

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openTransferDialog = agent => {
  currentAgent.value = agent;
  showEditPopup.value = false;
  showTransferDialog.value = true;
};
const closeLifecycle = () => {
  showTransferDialog.value = false;
};
const lifecycleDone = agentId => {
  if (currentAgent.value.id === agentId) closeLifecycle();
  store.dispatch('agents/get');
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="agent in filteredAgentList"
          :key="agent.email"
          class="flex flex-wrap justify-between items-start gap-4 py-4"
        >
          <div class="flex min-w-0 items-center gap-4">
            <Avatar
              :src="agent.thumbnail"
              :name="agent.name"
              :status="
                agent.is_disabled ? 'offline' : agent.availability_status
              "
              :size="40"
              hide-offline-status
            />
            <div class="flex min-w-0 flex-col gap-1.5 items-start">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ agent.name }}
              </span>
              <span
                v-if="agent.is_disabled"
                class="rounded-md bg-n-slate-3 px-2 py-0.5 text-xs text-n-slate-11"
                >{{ t('AGENT_LIFECYCLE.DISABLED_BADGE') }}</span
              >
              <div class="flex flex-wrap items-center gap-2">
                <span class="break-all text-body-main text-n-slate-11">
                  {{ agent.email }}
                </span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span
                  class="block w-fit text-body-main text-n-slate-11 relative"
                  :class="{
                    'hover:text-n-slate-12 group cursor-pointer':
                      agent.custom_role_id,
                  }"
                >
                  {{ getAgentRoleName(agent) }}

                  <div
                    class="absolute ltr:left-0 rtl:right-0 z-10 hidden w-[300px] bg-n-alpha-3 backdrop-blur-[100px] rounded-xl outline outline-1 outline-n-container shadow-lg top-14 md:top-12"
                    :class="{ 'group-hover:block': agent.custom_role_id }"
                  >
                    <div class="flex flex-col gap-1 p-4">
                      <span class="text-heading-3 text-n-slate-12">
                        {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                      </span>
                      <ul class="ltr:pl-4 rtl:pr-4 mb-0 list-disc">
                        <li
                          v-for="permission in getAgentRolePermissions(agent)"
                          :key="permission"
                          class="text-body-main text-n-slate-11"
                        >
                          {{
                            $t(
                              `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                            )
                          }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span
                  v-if="agent.confirmed"
                  class="text-body-main text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                </span>
                <span
                  v-if="!agent.confirmed"
                  class="text-body-main text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                </span>
              </div>
            </div>
          </div>
          <div class="ms-auto flex shrink-0 justify-end gap-3">
            <Button
              v-if="showEditAction(agent)"
              v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
              icon="i-woot-edit-pen"
              :aria-label="t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
              slate
              sm
              @click="openEditPopup(agent)"
            />
            <Button
              v-if="showDeleteAction(agent)"
              v-tooltip.top="t('AGENT_LIFECYCLE.REMOVE.BUTTON')"
              icon="i-woot-bin"
              :aria-label="t('AGENT_LIFECYCLE.REMOVE.BUTTON')"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openTransferDialog(agent)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent v-if="showAddPopup" @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        :is-master="currentAgent.is_master"
        :is-disabled="currentAgent.is_disabled"
        :can-manage-access="
          isMaster &&
          !currentAgent.is_master &&
          currentAgent.id !== currentUserId
        "
        :can-assign-master="currentAgent.can_assign_master"
        :can-reset-password="canResetPassword(currentAgent)"
        @close="hideEditPopup"
      />
    </woot-modal>

    <AgentLifecycleDialog
      v-if="showTransferDialog"
      :agent="currentAgent"
      @close="closeLifecycle"
      @done="lifecycleDone"
    />
  </SettingsLayout>
</template>
